import React from 'react';
import { Link } from 'react-router-dom';
import { useFetchStatus } from '@/hooks';
import { commonMessages, fallbackValues } from '@/constants';
import { getShortAnimeRating, valueOrDefault } from '@/utils';
import { appPaths, animeRatingOptions, animeTypeOptions } from '@/resources';
import {
  InfoRow,
  InfoValue,
  EmptyValueMessage,
  FormatDate,
  Loading,
  Score,
  Status,
  Tooltip,
  TooltipCommonProps,
  TooltipContentProps,
} from '@/components';
import './Tooltip.scss';

export const AnimeTooltip = (props: TooltipCommonProps<'anime'>) => {
  return (
    <Tooltip
      type="anime"
      tooltipContent={({ item, status }) => <AnimeTooltipContent item={item} status={status} />}
      {...props}
    />
  );
};

const AnimeTooltipContent: React.FC<TooltipContentProps<'anime'>> = ({ item, status }) => {
  const { isLoading, isError, isIdle } = useFetchStatus(status, true);

  if (isIdle) return null;

  if (isLoading)
    return (
      <div className="tooltip">
        <Loading />
      </div>
    );

  if (isError)
    return (
      <div className="tooltip">
        <EmptyValueMessage message={commonMessages.error} />
      </div>
    );

  if (!item)
    return (
      <div className="tooltip">
        <EmptyValueMessage message="No information" />
      </div>
    );

  return (
    <div className="tooltip">
      <div className="tooltip__labels">
        <Score className="tooltip__label tooltip__label--score" score={item.score} />
        <Status className="tooltip__label tooltip__label--status" status={item.status} />
      </div>
      <div className="tooltip__section">
        <h3 className="tooltip__title title visible-line visible-line--2">
          <Link to={appPaths.animeFull(item.mal_id)} title={item.title}>
            {item.title}
          </Link>
        </h3>
        {item.synopsis && (
          <p className="tooltip__text visible-line visible-line--5">{item.synopsis}</p>
        )}
      </div>
      <div className="tooltip__section">
        <ul className="tooltip__list">
          <InfoRow name="Type">
            {item.type ? (
              <InfoValue
                isLink
                to={appPaths.animeWithParams({
                  type: animeTypeOptions.find((obj) => obj.label === item.type)?.value,
                })}>
                {item.type}
              </InfoValue>
            ) : (
              <InfoValue>{fallbackValues.unknown}</InfoValue>
            )}

            {item.rating && (
              <InfoValue
                to={appPaths.animeWithParams({
                  rating: animeRatingOptions.find((obj) => obj.label === item.rating)?.value,
                })}
                isLink
                isPrimaryColor
                title={item.rating}>
                {getShortAnimeRating(item.rating)}
              </InfoValue>
            )}
          </InfoRow>

          <InfoRow name="Episodes">
            <InfoValue>
              {valueOrDefault(item.episodes, fallbackValues.mark)}
              {item.duration && item.duration !== 'Unknown' && (
                <>
                  &nbsp;&nbsp; {/* 2 spaces */}
                  <span>( {item.duration} )</span>
                </>
              )}
            </InfoValue>
          </InfoRow>

          <InfoRow name="Aired">
            <InfoValue>
              {item.aired.prop.from.year ? (
                <>
                  <FormatDate date={item.aired.prop.from} />
                  {item.aired.prop.to.year && (
                    <>
                      {' – '}
                      <FormatDate date={item.aired.prop.to} />
                    </>
                  )}
                </>
              ) : (
                fallbackValues.unknown
              )}
            </InfoValue>
          </InfoRow>

          {item.demographics.length > 0 && (
            <InfoRow name={item.demographics.length > 1 ? 'Demographics' : 'Demographic'}>
              {item.demographics.map((demographic) => (
                <InfoValue
                  key={demographic.mal_id}
                  to={appPaths.animeWithParams({ genres: demographic.mal_id.toString() })}
                  isLink>
                  {demographic.name}
                </InfoValue>
              ))}
            </InfoRow>
          )}

          <InfoRow name={item.genres.length > 1 ? 'Genres' : 'Genre'}>
            {item.genres.length > 0 ? (
              item.genres.map((genre) => (
                <InfoValue
                  key={genre.mal_id}
                  to={appPaths.animeWithParams({ genres: genre.mal_id.toString() })}
                  isLink>
                  {genre.name}
                </InfoValue>
              ))
            ) : (
              <InfoValue>{fallbackValues.unknown}</InfoValue>
            )}
          </InfoRow>

          {item.themes.length > 0 && (
            <InfoRow name={item.themes.length > 1 ? 'Themes' : 'Theme'}>
              {item.themes.map((theme) => (
                <InfoValue
                  key={theme.mal_id}
                  to={appPaths.animeWithParams({ genres: theme.mal_id.toString() })}
                  isLink>
                  {theme.name}
                </InfoValue>
              ))}
            </InfoRow>
          )}

          {item.studios.length > 0 && (
            <InfoRow name={item.studios.length > 1 ? 'Studios' : 'Studio'}>
              {item.studios.map((studio) => (
                <InfoValue key={studio.mal_id} to={appPaths.producerFull(studio.mal_id)} isLink>
                  {studio.name}
                </InfoValue>
              ))}
            </InfoRow>
          )}
        </ul>
      </div>
    </div>
  );
};
